export const CUSTOMER_EXPERIENCE_CENTER_URL = 'https://community.imprivata.com/s/';

export const ROLE_TYPE_ADMIN = 'admin' as const;

export const isAdminRole = (roleType?: string): boolean =>
  roleType?.toLowerCase() === ROLE_TYPE_ADMIN;

export const IMPRIVATA_DOMAIN = 'imprivata.id';

export enum PortalPath {
  HOME = '/',
  SETTINGS = '/settings',
  EAMC = '/eamc',
}

export const SettingsPath = {
  OVERVIEW: '/settings',
  ENTRA_ID_USERS: '/settings/entra-users',
  IDENTITY_USERS: '/settings/identity-users',
  SAML_IDP: '/settings/saml-idp',
  EAM_INTEGRATION: '/settings/eam-integration',
  VPAM_INTEGRATION: '/settings/vpam-integration',
  AIP_INTEGRATION: '/settings/aip-integration',
  IDV_CONNECTIONS: '/settings/oidc-integrations',
  SECURITY: '/settings/security',
  PASSWORD_POLICY: '/settings/password-policy',
  BRANDING_AND_LABELS: '/settings/branding-and-labels',
  BIOMETRIC_CONSENT: '/settings/biometric-consent',
  HUB_REPORTS: '/settings/hub-reports',
} as const satisfies Record<string, `${PortalPath.SETTINGS}` | `${PortalPath.SETTINGS}/${string}`>;

export type SettingsPath = (typeof SettingsPath)[keyof typeof SettingsPath];

export const LegacySettingsPath = {
  APPLICATIONS: '/settings/applications',
  ENDPOINTS: '/settings/endpoints',
  ADVANCED_SETTINGS: '/settings/advanced-settings',
  EPCS_ACTIVITY_REPORT: '/settings/epcs-activity-report',
  ENROLLMENT_ACTIVITY_REPORT: '/settings/enrollment-activity-report',
  LOGIN_ACTIVITY_REPORT: '/settings/login-activity-report',
  CUSTOMIZE: '/settings/customize',
} as const satisfies Record<string, `${PortalPath.SETTINGS}/${string}`>;

export type LegacySettingsPath = (typeof LegacySettingsPath)[keyof typeof LegacySettingsPath];

export const EamcPath = {
  ENTRA_ID_USERS: '/eamc/entra-users',
  IDENTITY_USERS: '/eamc/identity-users',
  APPLICATIONS: '/eamc/applications',
  ENDPOINTS: '/eamc/endpoints',
  WORKFLOWS: '/eamc/workflows',
  REPORTS: '/eamc/reports',
} as const satisfies Record<string, `${PortalPath.EAMC}/${string}`>;

export type EamcPath = (typeof EamcPath)[keyof typeof EamcPath];

export const LegacyEamcPath = { SECURITY: '/eamc/security' } as const satisfies Record<
  string,
  `${PortalPath.EAMC}/${string}`
>;

export type LegacyEamcPath = (typeof LegacyEamcPath)[keyof typeof LegacyEamcPath];

const ROUTE_AREAS = { global: PortalPath.SETTINGS, eamc: PortalPath.EAMC } as const;

export type RouteArea = keyof typeof ROUTE_AREAS;

export const isRouteArea = (value: unknown): value is RouteArea =>
  typeof value === 'string' && Object.hasOwn(ROUTE_AREAS, value);

const AREAS = Object.keys(ROUTE_AREAS).filter(isRouteArea);

export const routeAreaOf = (pathname: string): RouteArea | undefined =>
  AREAS.find(area => {
    const root = ROUTE_AREAS[area];
    return pathname === root || pathname.startsWith(`${root}/`);
  });

export const ENTRA_SETUP_ORIGIN_KEY = 'portal.entra-setup-origin';

export enum productTags {
  PATIENT_ACCESS = 'patient-secure-identity',
  SSO = 'sso',
  AIP = 'access-intel-platform',
  MAM = 'mobile-access-mgmt',
  EAM = 'enterprise-access-mgmt',
  ITDR = 'risk-based-access',
  VPAM = 'vendor-privileged-access-mgmt',
}

export const PORTAL_IDLE_TIMEOUT = 15; // in minutes
export const SELF_SERVICE_TIMEOUT = 5; // in minutes
export const IDLE_TIMEOUT_LOGOUT_EVENT = 'idle-timeout-logout';

export enum QueryKey {
  CLIENT_USER_SESSION = 'getClientUserSession',
  AUTHENTICATORS = 'authenticators',
  CONFIG_SETTINGS = 'config-settings',
  AM_PORTAL_CONFIG_SETTINGS = 'am-portal-config-settings',
  CREDENTIALS = 'credentials',
  FIND_IDP_USERS = 'findIdpUsers',
  FIND_USERS = 'findUsers',
  CONSENT_CHECK = 'consent-check',
  LEGAL_DOCUMENTS = 'legalDocuments',
  DIRECTORIES_CONFIG = 'directories-config',
  PASSWORD_EXPIRATION_INFO = 'password-expiration-info',
  PASSWORD_POLICY = 'password-policy',
  PROX_CARD_MAX_NUMBER = 'prox-card-max-number',
  FACE_DUPLICATE_PREVENTION = 'face-duplicate-prevention',
  TENANT_INFO = 'tenant-info',
  AUTHENTICATION_STRENGTH = 'authentication-strength',
  APP_INTEGRATION = 'app-integration',
  APP_CONFIG = 'app-config',
  ENDPOINTS = 'endpoints',
  ENDPOINT_INSTALLER_DATA = 'endpoint-installer-data',
  ENDPOINT_INSTALLATION_KEY = 'endpoint-installation-key',
  ENDPOINT_BEHAVIOR = 'endpoint-behavior',
  ENDPOINT_GROUPS = 'endpoint-groups',
  IDV_CONNECTIONS = 'idv-connections',
  IDV_CONNECTION = 'idv-connection',
  IDV_OIDC_DISCOVERY = 'idv-oidc-discovery',
  WORKSTATION_SETTINGS = 'workstation-settings',
  // Always used with the application's instanceKey appended, so the two rule-backed applications
  // hold independent caches and one application's save cannot invalidate the other's (REQ-020).
  AUTHN_RULES = 'authn-rules',
  RULE_SETS = 'rule-sets',
  RBA_POLICY = 'rba-policy',
  LANDING_PAGE = 'landing-page',
  STARTED_TRIAL = 'started-trial',
  APP_PROFILES = 'app-profiles',
  OIDC_APP_CONFIG = 'oidc-app-config',
  OIDC_APP_DISCOVERY = 'oidc-app-discovery',
}

export const FILES_DOWNLOAD_NAMES = {
  USERS_IMPORT_TEMPLATE: 'import-users-template.csv',
  USERS_IMPORT_ERROR_REPORT: 'import-error-report.csv',
  ENDPOINTS_REPORT: 'endpoints-report.csv',
} as const;
